import { useEffect, useMemo } from 'react'
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query'
import { useDebounce } from 'use-debounce'
import { useEventHistoryFilterStore } from './EventHistoryFilterStore'
import { groupEventsByDate } from './utils'
import useApiService from 'services/api'
import type { EventGroup } from './types'
import type { EventHistoryTab } from './EventHistoryAccordion'

const PAGE_SIZE = 20

/** Tab key → backend event_type value. */
const EVENT_TYPE_MAP: Record<EventHistoryTab, string> = {
  all: '',
  deals: 'deal',
  notes: 'notes',
  reminders: 'reminder',
  samples: 'samples',
  emails: 'email',
  documents: 'document',
  account_attributes: 'contactcompany',
}

interface UseEventHistoryQueryOptions {
  /** Company-scoped query (company page). */
  chainProxyId?: number
  /** Deal-scoped query (deal page). */
  dealId?: number
  /** Optional door filter (company page only). */
  doorProxyId?: number
  /** Currently selected sub-tab within the activity section. */
  selectedTab: EventHistoryTab
  /** Date range filter state. */
  dateRange?: { from: Date; to?: Date }
  /** Optional search term (debounced internally). */
  searchTerm?: string
  /** Filter to only show events with attachments. */
  hasAttachment?: boolean
}

interface UseEventHistoryQueryResult {
  groupedEvents: EventGroup[]
  fetchNextPage: () => void
  hasNextPage?: boolean
  isFetchingNextPage: boolean
  isLoading: boolean
  error: unknown
  totalCount: number
}

/**
 * Shared infinite-query hook for event history feeds.
 *
 * Supports both company-scoped (chainProxyId) and deal-scoped (dealId) queries.
 * Manages filter store integration, param building, pagination, and event grouping.
 */
export function useEventHistoryQuery({
  chainProxyId,
  dealId,
  doorProxyId,
  selectedTab,
  dateRange,
  searchTerm = '',
  hasAttachment,
}: UseEventHistoryQueryOptions): UseEventHistoryQueryResult {
  const api = useApiService()
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)

  const { eventTypes, teamMemberIds, criteria } = useEventHistoryFilterStore()

  // Clear event type filters when switching to a specific tab (not "all")
  useEffect(() => {
    if (selectedTab !== 'all' && eventTypes.length > 0) {
      useEventHistoryFilterStore.getState().setPendingEventTypes([])
      useEventHistoryFilterStore.getState().applyFilters()
    }
  }, [selectedTab, eventTypes.length])

  const buildQueryParams = (
    pageParam: number
  ): Record<string, string | number | string[] | number[]> => {
    const params: Record<string, string | number | string[] | number[]> = {
      page_size: PAGE_SIZE,
      page: pageParam,
    }

    if (doorProxyId) {
      params.door_proxy_id = doorProxyId
    }

    if (debouncedSearchTerm.trim()) {
      params.search = debouncedSearchTerm.trim()
    }

    // Prioritize "More Filters" modal over tab selection
    if (eventTypes.length > 0) {
      params.event_type = eventTypes
    } else if (selectedTab !== 'all') {
      params.event_type = EVENT_TYPE_MAP[selectedTab]
    }

    if (teamMemberIds.length > 0) {
      params.user = teamMemberIds
    }

    if (eventTypes.length > 0 || teamMemberIds.length > 0) {
      params.filter_type = criteria
    }

    if (dateRange?.from) {
      params.created_at_greater = dateRange.from.toISOString()
    }
    if (dateRange?.to) {
      params.created_at_less = dateRange.to.toISOString()
    }

    if (hasAttachment) {
      params.has_attachment = 1
    }

    return params
  }

  const isDeal = !!dealId
  const entityId = dealId ?? chainProxyId
  const queryKeyPrefix = isDeal ? 'dealActivity' : 'companyActivity'

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    error,
  } = useInfiniteQuery({
    queryKey: [
      queryKeyPrefix,
      entityId,
      doorProxyId,
      selectedTab,
      debouncedSearchTerm,
      dateRange,
      eventTypes,
      teamMemberIds,
      criteria,
      hasAttachment,
    ],
    queryFn: ({ pageParam }) => {
      const params = buildQueryParams(pageParam)
      if (isDeal) {
        return api.getDealActivityList(dealId!, params)
      }
      return api.getCompanyActivityList(chainProxyId!, params)
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.results.length,
        0
      )
      if (totalFetched < lastPage.count) {
        return allPages.length + 1
      }
      return undefined
    },
    enabled: !!entityId,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // Keep showing the previous tab/filter's data while the new query
    // resolves. The list otherwise collapses to its `min-h-[100px]`
    // empty state on every tab switch, shifting the whole page until
    // the new data arrives. With placeholder data, the loading overlay
    // dims the old content in place and the layout stays put.
    placeholderData: keepPreviousData,
  })

  const groupedEvents = useMemo(() => {
    if (!data?.pages) return []
    const allEvents = data.pages.flatMap((page) => page.results)

    const filteredEvents = allEvents.filter((event) => {
      // Filter out document events with empty field_history_objects
      if (event.content_type?.model === 'document') {
        return (
          event.field_history_objects && event.field_history_objects.length > 0
        )
      }
      // Filter out email UPDATE events — only show CREATE (new emails)
      if (
        event.content_type?.model === 'emailmessage' &&
        event.action_type === 'UPDATE'
      ) {
        return false
      }
      return true
    })

    return groupEventsByDate(filteredEvents)
  }, [data?.pages])

  const totalCount = data?.pages?.[0]?.count ?? 0

  // With placeholderData enabled, RQ's `isLoading` is only true on the
  // very first fetch. Tab/filter changes set `isFetching` while showing
  // the previous data as placeholder. We want the list overlay in both
  // cases, but NOT during pagination ("Load More"), which has its own
  // inline spinner.
  const isLoading = isFetching && !isFetchingNextPage

  return {
    groupedEvents,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    totalCount,
  }
}
